(function () {
  const root = document.querySelector('[data-sd-custom-quote]');
  if (!root) return;

  const form = root.querySelector('#SDCustomQuoteForm');
  const searchInput = root.querySelector('[data-sd-quote-search]');
  if (!form || !searchInput) return;

  const thresholdCents = Math.max(0, Number(root.dataset.threshold || 1299) * 100);
  const minimumUnits = Math.max(1, Number(root.dataset.minimumUnits || 12));
  const shopRoot = window.Shopify && window.Shopify.routes ? window.Shopify.routes.root : '/';
  const currency = window.Shopify && window.Shopify.currency ? window.Shopify.currency.active : 'USD';
  const state = { items: [] };

  const elements = {
    results: root.querySelector('[data-sd-quote-results]'),
    searchStatus: root.querySelector('[data-sd-quote-search-status]'),
    list: root.querySelector('[data-sd-quote-list]'),
    empty: root.querySelector('[data-sd-quote-empty]'),
    count: root.querySelector('[data-sd-quote-count]'),
    total: root.querySelector('[data-sd-quote-total]'),
    progress: root.querySelector('[data-sd-quote-progress]'),
    progressBar: root.querySelector('[data-sd-quote-progress-bar]'),
    progressLabel: root.querySelector('[data-sd-quote-progress-label]'),
    message: root.querySelector('[data-sd-quote-message]'),
    submit: root.querySelector('[data-sd-quote-submit]'),
    productsField: root.querySelector('[data-sd-quote-products-field]'),
    totalField: root.querySelector('[data-sd-quote-total-field]')
  };

  const money = (cents) => {
    try {
      return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(cents / 100);
    } catch (error) {
      return '$' + (cents / 100).toFixed(2);
    }
  };

  const debounce = (callback, wait) => {
    let timeout;
    return function () {
      const args = arguments;
      window.clearTimeout(timeout);
      timeout = window.setTimeout(() => callback.apply(null, args), wait);
    };
  };

  const normalizeProductUrl = (url) => {
    const parsed = new URL(url, window.location.origin);
    return parsed.pathname;
  };

  const inferPackUnits = (title) => {
    const lowerTitle = String(title || '').toLowerCase();
    if (lowerTitle.includes('single') || lowerTitle === 'default title') return 1;
    if (lowerTitle.includes('dozen')) return 12;
    const match = lowerTitle.match(/(?:box|case|pack)?[^0-9]{0,5}(\d{1,3})\s*(?:units?|pcs?|pieces?|ea)?/i);
    return match ? Math.max(1, Number(match[1])) : 1;
  };

  const getReferenceVariant = (product) => {
    const available = product.variants.filter((variant) => variant.available !== false);
    const candidates = available.length ? available : product.variants;
    const dozen = candidates.find((variant) => String(variant.title).toLowerCase().includes('dozen'));
    const single = candidates.find((variant) => String(variant.title).toLowerCase().includes('single'));
    const variant = dozen || single || candidates[0];
    if (!variant) return null;
    const packUnits = inferPackUnits(variant.title);
    return {
      id: variant.id,
      title: variant.title,
      sku: variant.sku || '',
      unitPriceCents: Math.round(Number(variant.price || 0) / packUnits),
      packUnits
    };
  };

  const serializeRequest = () => state.items.map((item, index) => [
    `${index + 1}. ${item.title}`,
    `Product URL: ${window.location.origin}${item.url}`,
    `Reference SKU: ${item.sku || 'Not provided'}`,
    `Requested units: ${item.quantity}`,
    `Reference unit price: ${money(item.unitPriceCents)}`,
    `Reference subtotal: ${money(item.unitPriceCents * item.quantity)}`
  ].join('\n')).join('\n\n');

  const updateSummary = () => {
    const totalCents = state.items.reduce((sum, item) => sum + (item.unitPriceCents * item.quantity), 0);
    const allQuantitiesValid = state.items.length > 0 && state.items.every((item) => item.quantity >= minimumUnits);
    const qualifies = allQuantitiesValid && totalCents >= thresholdCents;
    const productLabel = state.items.length === 1 ? 'product' : 'products';
    const percent = thresholdCents ? Math.min(100, (totalCents / thresholdCents) * 100) : 100;

    elements.count.textContent = `${state.items.length} ${productLabel} selected`;
    elements.total.textContent = money(totalCents);
    elements.progress.setAttribute('aria-valuenow', String(Math.round(totalCents / 100)));
    elements.progress.setAttribute('aria-valuetext', `${Math.round(percent)}% of the ${money(thresholdCents)} minimum`);
    elements.progressBar.style.width = '100%';
    elements.progressBar.style.transform = `scaleX(${percent / 100})`;
    if (elements.progressLabel) elements.progressLabel.textContent = `${Math.round(percent)}%`;
    elements.message.classList.toggle('is-qualified', qualifies);

    if (!state.items.length) {
      elements.message.textContent = 'Add products to begin your custom quote request.';
    } else if (!allQuantitiesValid) {
      elements.message.textContent = `Each product requires at least ${minimumUnits} units.`;
    } else if (totalCents < thresholdCents) {
      elements.message.textContent = `Add ${money(thresholdCents - totalCents)} more to reach the custom-pricing minimum.`;
    } else {
      elements.message.textContent = 'Your request qualifies for custom pricing review.';
    }

    elements.submit.disabled = !qualifies;
    elements.productsField.value = serializeRequest();
    elements.totalField.value = `${money(totalCents)} based on current reference pricing`;
  };

  const renderItems = () => {
    elements.list.querySelectorAll('[data-sd-quote-item]').forEach((node) => node.remove());
    elements.empty.hidden = state.items.length > 0;

    state.items.forEach((item) => {
      const article = document.createElement('article');
      article.className = 'sd-quote-item';
      article.dataset.sdQuoteItem = item.handle;

      const image = document.createElement('img');
      image.className = 'sd-quote-item__image';
      image.src = item.image || '';
      image.alt = '';
      image.loading = 'lazy';

      const copy = document.createElement('div');
      const title = document.createElement('strong');
      title.className = 'sd-quote-item__title';
      title.textContent = item.title;
      const meta = document.createElement('span');
      meta.className = 'sd-quote-item__meta';
      meta.textContent = `${money(item.unitPriceCents)} reference price per unit${item.sku ? ` · SKU ${item.sku}` : ''}`;
      copy.append(title, meta);

      const field = document.createElement('div');
      field.className = 'sd-quote-item__field';
      const label = document.createElement('label');
      label.textContent = 'Desired units';
      label.htmlFor = `SDQuoteQuantity-${item.handle}`;
      const input = document.createElement('input');
      input.type = 'number';
      input.id = label.htmlFor;
      input.min = String(minimumUnits);
      input.step = '1';
      input.value = String(item.quantity);
      input.inputMode = 'numeric';
      input.addEventListener('input', () => {
        item.quantity = Math.max(0, Math.floor(Number(input.value) || 0));
        subtotal.textContent = money(item.unitPriceCents * item.quantity);
        updateSummary();
      });
      input.addEventListener('change', () => {
        item.quantity = Math.max(minimumUnits, Math.floor(Number(input.value) || minimumUnits));
        input.value = String(item.quantity);
        subtotal.textContent = money(item.unitPriceCents * item.quantity);
        updateSummary();
      });
      field.append(label, input);

      const subtotal = document.createElement('div');
      subtotal.className = 'sd-quote-item__subtotal';
      subtotal.textContent = money(item.unitPriceCents * item.quantity);

      const remove = document.createElement('button');
      remove.className = 'sd-quote-item__remove';
      remove.type = 'button';
      remove.textContent = 'Remove';
      remove.setAttribute('aria-label', `Remove ${item.title}`);
      remove.addEventListener('click', () => {
        state.items = state.items.filter((candidate) => candidate.handle !== item.handle);
        renderItems();
      });

      article.append(image, copy, field, subtotal, remove);
      elements.list.appendChild(article);
    });

    updateSummary();
  };

  const addProduct = async (url) => {
    const productPath = normalizeProductUrl(url);
    const handle = productPath.split('/').filter(Boolean).pop();
    if (!handle || state.items.some((item) => item.handle === handle)) {
      elements.searchStatus.textContent = 'That product is already in your request.';
      return;
    }

    elements.searchStatus.textContent = 'Adding product…';
    try {
      const response = await fetch(`${productPath}.js`, { headers: { Accept: 'application/json' } });
      if (!response.ok) throw new Error('Product request failed');
      const product = await response.json();
      const reference = getReferenceVariant(product);
      if (!reference || reference.unitPriceCents <= 0) throw new Error('No usable reference price');

      state.items.push({
        handle,
        title: product.title,
        url: productPath,
        image: product.featured_image || (product.images && product.images[0]) || '',
        sku: reference.sku,
        referenceVariantId: reference.id,
        referenceVariantTitle: reference.title,
        unitPriceCents: reference.unitPriceCents,
        quantity: minimumUnits
      });
      elements.searchStatus.textContent = `${product.title} added.`;
      searchInput.value = '';
      elements.results.replaceChildren();
      renderItems();
    } catch (error) {
      elements.searchStatus.textContent = 'We could not add that product. Please try another product or include it in your notes.';
    }
  };

  const renderSearchResults = (products) => {
    elements.results.replaceChildren();
    products.forEach((product) => {
      const row = document.createElement('div');
      row.className = 'sd-quote-search-result';

      const image = document.createElement('img');
      image.src = product.image || '';
      image.alt = '';
      image.loading = 'lazy';

      const title = document.createElement('strong');
      title.textContent = product.title;

      const button = document.createElement('button');
      button.type = 'button';
      button.textContent = state.items.some((item) => item.handle === normalizeProductUrl(product.url).split('/').filter(Boolean).pop()) ? 'Added' : 'Add';
      button.disabled = button.textContent === 'Added';
      button.addEventListener('click', () => addProduct(product.url));

      row.append(image, title, button);
      elements.results.appendChild(row);
    });
  };

  const searchProducts = debounce(async () => {
    const query = searchInput.value.trim();
    if (query.length < 2) {
      elements.results.replaceChildren();
      elements.searchStatus.textContent = query ? 'Enter at least 2 characters.' : '';
      return;
    }

    elements.searchStatus.textContent = 'Searching…';
    try {
      const params = new URLSearchParams({
        q: query,
        'resources[type]': 'product',
        'resources[limit]': '6',
        'resources[options][unavailable_products]': 'hide'
      });
      const response = await fetch(`${shopRoot}search/suggest.json?${params.toString()}`, { headers: { Accept: 'application/json' } });
      if (!response.ok) throw new Error('Search failed');
      const payload = await response.json();
      const products = payload.resources && payload.resources.results ? payload.resources.results.products || [] : [];
      renderSearchResults(products);
      elements.searchStatus.textContent = products.length ? `${products.length} products found.` : 'No matching products found.';
    } catch (error) {
      elements.results.replaceChildren();
      elements.searchStatus.textContent = 'Product search is temporarily unavailable. Please try again.';
    }
  }, 300);

  searchInput.addEventListener('input', searchProducts);

  form.addEventListener('submit', (event) => {
    updateSummary();
    if (elements.submit.disabled || !elements.productsField.value) {
      event.preventDefault();
      elements.message.textContent = 'Complete the product and minimum-value requirements before submitting.';
      elements.message.focus();
    }
  });

  const requestedHandle = new URLSearchParams(window.location.search).get('product');
  if (requestedHandle && /^[a-z0-9][a-z0-9-]*$/i.test(requestedHandle)) {
    addProduct(`${shopRoot}products/${requestedHandle}`);
  } else {
    updateSummary();
  }
})();
